"use client";

import { Box } from "@chakra-ui/react";
import NextLink from "next/link";
import type { ReactElement } from "react";
import { IconButton, TouchAwareTooltip } from "@/components/ui";
import { useInteractiveTokens } from "@/lib/theme/tokens/interactive";
import { useThemeTokens } from "@/lib/theme/tokens";

export interface SinglePageHeaderIconButtonProps {
  label: string;
  icon: ReactElement;
  onClick?: () => void;
  href?: string;
  isDisabled?: boolean;
  tooltip?: string;
  tone?: "default" | "destructive";
  "data-testid"?: string;
}

export function SinglePageHeaderIconButton({
  label,
  icon,
  onClick,
  href,
  isDisabled = false,
  tooltip,
  tone = "default",
  "data-testid": dataTestId,
}: SinglePageHeaderIconButtonProps) {
  const theme = useThemeTokens();
  const interactive = useInteractiveTokens();
  const isDestructive = tone === "destructive";

  const visualProps = {
    variant: "ghost" as const,
    size: "sm" as const,
    h: "44px",
    w: "44px",
    minW: "44px",
    "aria-label": label,
    icon,
    color: theme.text.tertiary,
    borderWidth: "1px",
    borderColor: theme.borderGlass.default,
    bg: "transparent",
    "data-testid": dataTestId,
    sx: {
      "@media (hover: hover)": {
        "&:hover:not(:disabled)": isDestructive
          ? {
              bg: interactive.neutral.bg,
              borderColor: "red.400",
              color: "red.400",
            }
          : {
              bg: interactive.neutral.bg,
              borderColor: theme.accent.primary,
              color: theme.text.primary,
            },
      },
    },
  };

  const control =
    href && !isDisabled ? (
      <IconButton {...visualProps} as={NextLink} href={href} />
    ) : (
      <IconButton {...visualProps} onClick={onClick} isDisabled={isDisabled} />
    );

  return (
    <TouchAwareTooltip
      label={tooltip ?? label}
      placement="top"
      openDelay={300}
    >
      <Box display="inline-flex">{control}</Box>
    </TouchAwareTooltip>
  );
}
